import useAccountData from 'src/hooks/useAccountData';
import { useCurrentAccountPositions } from 'src/hooks/query/accountTradingData/usePositions';
import { useCurrentAccountMarginInfo } from 'src/hooks/query/accountTradingData/useMarginInfo';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import {
	BN,
	BigNum,
	PRICE_PRECISION_EXP,
	QUOTE_PRECISION_EXP,
} from '@velocity-exchange/sdk';
import {
	MarketId,
	OpenPosition,
	convertMarginRatioToLeverage,
	calculatePnlPctFromPosition,
	convertLeverageToMarginRatio,
} from '@velocity-exchange/common';
import useMarkPrice from 'src/hooks/useMarkPrice';
import { getReferralLink } from 'src/utils/referral';
import { getMarginUsedForPosition } from 'src/utils/position';
import useReferrerNameAccounts from 'src/hooks/useReferrerNameAccounts';
import { ZERO_PRICE_BIGNUM } from 'src/constants/math';
import { PnlModalCardPreviewVariantProps } from 'src/components/Modals/PnlModal/types';
import usePnlModalCardImagePaths from 'src/components/Modals/PnlModal/hooks/usePnlModalCardImagePaths';
import Env from 'src/environmentVariables/EnvironmentVariables';
import usePnlModalConfetti from 'src/components/Modals/PnlModal/hooks/usePnlModalConfetti';
import useBalanceAssetSymbolsByQuoteValueDesc from 'src/components/Modals/PnlModal/hooks/useBalanceAssetSymbolsByQuoteValueDesc';
import { getMarketPriceDecimals } from 'src/constants/marketDecimalConfig';
import dayjs from 'dayjs';
import { useMemo, useRef } from 'react';
import {
	STANDARD_DATE_FORMAT,
	STANDARD_DAY_MONTH_FORMAT,
} from 'src/constants/dates';
import useGetDisplayLeverageForMarket from 'src/hooks/useGetDisplayLeverageForMarket';

interface UsePnlModalCardPreview {
	isMobileScreenSize: boolean;
	referralLink: string;
	isPositivePnl: boolean;
	backgroundImagePaths: string[];
	pnlDisplayFontSize: string;
	pnlDisplay: string;
	accountName?: string;
	period?: string;
	marketSymbol?: string;
	direction?: string;
	leverageString?: string;
	entryExit?: { label: string; value: string }[];
	balanceAssetSymbolsByQuoteValueDesc?: string[];
}

export default function usePnlModalCardPreview({
	variant,
	positionData,
	portfolioData,
}: PnlModalCardPreviewVariantProps): UsePnlModalCardPreview {
	const { referrerNameAccounts } = useReferrerNameAccounts();
	const isMobileScreenSize = useIsMobileScreenSize();
	const balanceAssetSymbolsByQuoteValueDesc =
		useBalanceAssetSymbolsByQuoteValueDesc({
			isIsolated: positionData?.isIsolated,
		});

	const userAccountData = useAccountData();
	const openPerpPositions = useCurrentAccountPositions();
	const marginInfo = useCurrentAccountMarginInfo();

	const positionMarketId = useMemo(
		() => MarketId.createPerpMarket(positionData?.position.marketIndex ?? 0),
		[positionData?.position.marketIndex]
	);
	const currentMarkPrice = useMarkPrice(positionMarketId);

	const referralLink = getReferralLink(
		referrerNameAccounts.accounts[0]?.decodedName
	);

	// Snapshot closed position data on first render to prevent the race condition
	// where syncOpenPositions clears pnlInfo while the modal is open.
	const closedPositionSnapshotRef = useRef<{
		leverage: number;
		marginUsed: BN | undefined;
	} | null>(null);

	// If this is an open position, we need to use the current, dynamic PnL rather
	// than the static PnL passed in via props.
	const currentPosition = (() => {
		if (variant === 'portfolio-performance') {
			return undefined;
		}

		return openPerpPositions.find(
			(pos) =>
				pos.marketIndex === positionData?.position.marketIndex &&
				pos.direction === positionData?.position.direction
		);
	})();

	const currentPositionDisplayLeverage = useGetDisplayLeverageForMarket(
		currentPosition?.marketIndex ?? 0
	);

	const isPositionClosed =
		positionData?.triggeredFrom !== 'open_positions_table';
	const pnlFromProps =
		variant === 'portfolio-performance' ? portfolioData.pnl : positionData.pnl;
	const currentPnl =
		variant === 'portfolio-performance' || isPositionClosed
			? pnlFromProps
			: BigNum.from(currentPosition?.pnlVsMark, QUOTE_PRECISION_EXP).toNum();

	const netPnl = BigNum.fromPrint(currentPnl.toString(), QUOTE_PRECISION_EXP);

	const isPositivePnl = netPnl?.toNum() >= 0;
	const pnlSign = isPositivePnl ? '+' : '';

	const backgroundImagePaths = usePnlModalCardImagePaths({
		marketIndex: positionData?.position.marketIndex,
		isPositivePnl,
		variant,
	});

	usePnlModalConfetti({
		shouldShow:
			isPositivePnl && positionData?.triggeredFrom === 'position_closed',
	});

	const notionalPnlDisplay = `${pnlSign}${netPnl.toNotional()}`;

	// The PnL value can be quite large when displaying in notional form,
	// so we use a dynamic font size based on it.
	const pnlDisplayFontSize = (() => {
		// PnL < 10_000 (or neg):
		if (netPnl.toNum() < 10_000 && netPnl.toNum() > -10_000) {
			return isMobileScreenSize ? 'text-3xl' : 'text-5xl';
		}

		// 10_000 <= PnL < 10_000_000 (or neg):
		if (netPnl.toNum() < 10_000_000 && netPnl.toNum() > -10_000_000) {
			return isMobileScreenSize ? 'text-2xl' : 'text-4xl';
		}

		// PnL >= 10_000_000 (or neg):
		return isMobileScreenSize ? 'text-lg' : 'text-3xl';
	})();

	// Returned for both variants.
	const commonProps = {
		isMobileScreenSize,
		referralLink,
		isPositivePnl,
		backgroundImagePaths,
		pnlDisplayFontSize,
	};

	const dateFormat =
		portfolioData?.period === '24h'
			? `h:mm A ${STANDARD_DAY_MONTH_FORMAT}`
			: STANDARD_DATE_FORMAT;
	// Only do this on mount as we're not dynamically updating data.
	const endDate = useMemo(() => dayjs().utc().format(dateFormat), [dateFormat]);

	if (variant === 'portfolio-performance') {
		const startDate = dayjs(portfolioData.startOfPeriodTs * 1000)
			.utc()
			.format(dateFormat);

		return {
			...commonProps,
			accountName: portfolioData.accountName,
			pnlDisplay: notionalPnlDisplay,
			period: `${startDate} - ${endDate}`,
		};
	}

	const { position, actualExitPrice: exitPrice, triggeredFrom } = positionData;

	const {
		baseSize,
		marketSymbol,
		entryPrice,
		direction: directionProp,
	} = position;
	const direction = directionProp ?? (baseSize.isNeg() ? 'short' : 'long');

	const markPrice = currentMarkPrice ?? ZERO_PRICE_BIGNUM;

	const leverageBeforeClose =
		userAccountData?.pnlInfo?.[position.marketIndex]?.leverageBeforeClose;
	const collateralBeforeClose =
		userAccountData?.pnlInfo?.[position.marketIndex]?.collateralBeforeClose;

	const liveLeverage = (() => {
		if (currentPosition && currentPositionDisplayLeverage) {
			return Math.round(currentPositionDisplayLeverage);
		}

		if (leverageBeforeClose && collateralBeforeClose) {
			return leverageBeforeClose;
		}

		// Get leverage from the position's maxMarginRatio
		if (currentPosition?.maxMarginRatio) {
			return convertMarginRatioToLeverage(currentPosition.maxMarginRatio) ?? 1;
		}

		// Try to get leverage from the position data passed to the modal
		if (positionData.position.maxMarginRatio) {
			return (
				convertMarginRatioToLeverage(positionData.position.maxMarginRatio) ?? 1
			);
		}

		// Fallback to user account leverage if position data is not available
		return marginInfo?.leverage ?? 0;
	})();

	const liveMarginUsed = (() => {
		const userClient = userAccountData?.client;

		if (isPositionClosed && collateralBeforeClose) {
			return collateralBeforeClose;
		}

		// For closed positions, don't call getMarginUsedForPosition — it returns
		// BN(0) for zero-size positions, which is truthy and causes
		// calculatePnlPctFromPosition to compute 0%. Instead, return undefined
		// so the function falls back to computing margin from quoteEntryAmount / leverage.
		if (isPositionClosed) {
			return undefined;
		}

		return userClient
			? getMarginUsedForPosition(userClient, position.marketIndex, false)
			: undefined;
	})();

	// For closed positions, snapshot leverage and margin on the first render
	// so that the values survive syncOpenPositions clearing pnlInfo.
	if (isPositionClosed && !closedPositionSnapshotRef.current) {
		closedPositionSnapshotRef.current = {
			leverage: liveLeverage,
			marginUsed: liveMarginUsed,
		};
	}

	const leverage = isPositionClosed
		? (closedPositionSnapshotRef.current?.leverage ?? liveLeverage)
		: liveLeverage;
	const marginUsed = isPositionClosed
		? (closedPositionSnapshotRef.current?.marginUsed ?? liveMarginUsed)
		: liveMarginUsed;

	// We don't show any leverage for position history table.
	const leverageString =
		triggeredFrom === 'position_history_table'
			? ''
			: `${leverage.toFixed(2).replace(/\.0+$/, '')}x`;

	const leveragedPnlPercentageNum = calculatePnlPctFromPosition(
		netPnl.val,
		{
			...position,
			maxMarginRatio: convertLeverageToMarginRatio(leverage),
		} as OpenPosition,
		marginUsed
	);

	const pnlPercentageToDisplay = leveragedPnlPercentageNum.toLocaleString(
		Env.locale,
		{
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}
	);

	// We show the notional PnL for position history table, otherwise we show the percentage.
	const pnlDisplay = (() => {
		if (triggeredFrom === 'position_history_table') {
			return notionalPnlDisplay;
		}

		return `${pnlSign}${pnlPercentageToDisplay}%`;
	})();

	const entryExit = (() => {
		return [
			{
				label: 'Entry Price',
				value: formatPrice(
					BigNum.from(entryPrice, PRICE_PRECISION_EXP).toNum(),
					marketSymbol
				),
			},
			{
				label: isPositionClosed ? 'Exit Price' : 'Mark Price',
				value: formatPrice(
					isPositionClosed ? exitPrice : markPrice.toNum(),
					marketSymbol
				),
			},
		];
	})();

	return {
		...commonProps,
		marketSymbol,
		direction,
		leverageString,
		pnlDisplay,
		entryExit,
		balanceAssetSymbolsByQuoteValueDesc,
	};
}

function formatPrice(price: number, marketSymbol: string) {
	const decimals = getMarketPriceDecimals(marketSymbol);
	return price.toLocaleString(Env.locale, {
		minimumFractionDigits: decimals,
		maximumFractionDigits: decimals,
	});
}
